import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRocket } from '@tabler/icons-react';
import { promoteCandidates, type PromoteCandidatesResult } from '../api/candidates';
import type { CandidateEntry } from '../api/types';

interface PromoteCandidateCardProps {
    candidate: CandidateEntry;
    onPromoted: () => void;
}

/**
 * Promote-to-catalog action card.
 *
 * Promotes this single candidate through the bulk endpoint (a one-element
 * `ids`). The server curates it in place from its OWN stored data and auto-links
 * to an existing company on a domain/name match (VI-549) — so there is no 409
 * conflict flow to resolve here. A nameless candidate is blocked client-side;
 * any server-side failure comes back in the result's `failed` bucket. On success
 * we invalidate the candidate + vendor caches and call `onPromoted`.
 */
export const PromoteCandidateCard = ({
    candidate,
    onPromoted,
}: PromoteCandidateCardProps): React.JSX.Element => {
    const queryClient = useQueryClient();

    // Promote requires a non-empty name; the server 400s otherwise, but block it
    // client-side for a clearer UX.
    const cannotPromote = !candidate.name;

    const promoteMutation = useMutation({
        mutationFn: () => promoteCandidates([candidate.id]),
        onSuccess: (result: PromoteCandidatesResult) => {
            const failure = result.failed[0];
            if (failure) {
                notifications.show({
                    color: 'red',
                    title: 'Failed to promote candidate',
                    message: failure.error,
                });
                return;
            }

            const promoted = result.promoted[0];
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            notifications.show({
                color: 'green',
                title: 'Candidate promoted',
                message: promoted
                    ? `New vendor: ${promoted.vendor_directory_id}`
                    : 'Candidate promoted.',
            });
            onPromoted();
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Failed to promote candidate',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const handlePromote = useCallback(() => {
        promoteMutation.mutate();
    }, [promoteMutation]);

    return (
        <Card withBorder radius="md" padding="lg" data-testid="PromoteCandidateCard">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Box>
                        <Title order={4}>Promote to catalog</Title>
                        <Text size="sm" c="dimmed" mt={4}>
                            Curates this candidate into the catalog (vendor + product) from its own
                            stored data, auto-linking to an existing company when one matches.
                        </Text>
                    </Box>
                    <Button
                        leftSection={<IconRocket size={16} />}
                        onClick={handlePromote}
                        loading={promoteMutation.isPending}
                        disabled={cannotPromote}
                        data-testid="PromoteCandidateButton"
                    >
                        Promote
                    </Button>
                </Group>

                {cannotPromote ? (
                    <Alert
                        icon={<IconAlertTriangle size={16} />}
                        color="yellow"
                        title="Candidate is missing a name"
                        data-testid="PromoteCandidateNoName"
                    >
                        Promote requires a non-empty name. Fix the upstream record before this row
                        can be promoted.
                    </Alert>
                ) : null}
            </Stack>
        </Card>
    );
};
